import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Logo } from '../components/Logo';
import { formatApiError } from '../lib/api';
import { googleSignIn } from '../components/AuthCallback';
import { Mail, Lock, Eye, EyeOff } from 'lucide-react';
import { usePageSEO } from '../lib/usePageSEO';

export default function Login() {
  usePageSEO({
    title: 'Sign in — MyLeaf Legacy',
    description: 'Sign in to your MyLeaf Legacy account to manage memorials, the Family Vault, and partner dashboards.',
    path: '/login',
  });
  const [email, setEmail] = useState(() => {
    try { return localStorage.getItem('myleaf.remember.email') || ''; } catch { return ''; }
  });
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [remember, setRemember] = useState(() => {
    try { return localStorage.getItem('myleaf.remember.email') != null; } catch { return false; }
  });
  const [err, setErr] = useState(null);
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();

  const submit = async (e) => {
    e.preventDefault();
    setErr(null); setLoading(true);
    try {
      const u = await login(email, password);
      // Persist (or forget) the email per the user's "remember me" choice.
      try {
        if (remember) localStorage.setItem('myleaf.remember.email', email);
        else localStorage.removeItem('myleaf.remember.email');
      } catch { /* storage disabled — silently ignore */ }
      // Professionals go to their application/professional dashboard; everyone else to memorials.
      const dest = loc.state?.from
        || (u && u.account_type === 'professional' ? '/professional' : '/dashboard');
      nav(dest, { replace: true });
    } catch (e2) {
      setErr(formatApiError(e2));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid md:grid-cols-2">
      {/* Left: copy */}
      <div className="relative hidden md:flex flex-col justify-between p-10 bg-[hsl(var(--primary))] text-[#FDFBF7] overflow-hidden">
        <Link to="/" className="inline-flex" data-testid="login-side-logo-link">
          <Logo tone="cream" size={176} />
        </Link>
        <div>
          <div className="ml-overline !text-[#D9B775] !font-semibold">Welcome back</div>
          <h1 className="font-serif text-5xl mt-4 leading-tight font-semibold">Continue tending to the memories your family carries.</h1>
          <p className="mt-5 text-[#FDFBF7]/90 max-w-md font-medium">
            Sign in to update a memorial, approve Family Vault requests, or add a new story to the olive tree.
          </p>
        </div>
        <div className="text-xs text-[#FDFBF7]/70 font-medium">© MyLeaf Legacy — Forever Here</div>
      </div>

      {/* Right: form */}
      <div className="flex items-center justify-center p-6 sm:p-10">
        <div className="w-full max-w-md">
          <div className="md:hidden mb-8"><Link to="/"><Logo size={128} /></Link></div>
          <div className="ml-overline !text-[hsl(var(--primary))] !font-semibold">Sign in</div>
          <h2 className="font-serif text-4xl mt-2 text-[hsl(var(--primary))] font-semibold">Welcome back.</h2>
          <p className="text-sm text-[hsl(var(--primary)/0.78)] mt-2 font-medium">We're glad you're here.</p>

          <button
            onClick={() => googleSignIn(loc.state?.from || '/dashboard')}
            className="mt-8 w-full ml-btn-outline !py-3 !justify-center"
            data-testid="login-google-btn"
          >
            <svg viewBox="0 0 48 48" className="w-4 h-4"><path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.6 32.2 29.2 35 24 35c-6.1 0-11-4.9-11-11s4.9-11 11-11c2.8 0 5.4 1 7.4 2.8l5.7-5.7C33.8 7 29.1 5 24 5 13.5 5 5 13.5 5 24s8.5 19 19 19 19-8.5 19-19c0-1.2-.1-2.4-.4-3.5z"/><path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 16.3 19 13 24 13c2.8 0 5.4 1 7.4 2.8l5.7-5.7C33.8 7 29.1 5 24 5 16.3 5 9.7 9 6.3 14.7z"/><path fill="#4CAF50" d="M24 43c5 0 9.7-1.9 13.2-5.1l-6.1-5.1C29 34.1 26.6 35 24 35c-5.2 0-9.6-2.8-11.3-7l-6.5 5C9.6 39 16.2 43 24 43z"/><path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.3-2.2 4.2-4.1 5.7l6.1 5.1C40.9 35.9 43 30.6 43 24c0-1.2-.1-2.4-.4-3.5z"/></svg>
            Continue with Google
          </button>

          <div className="ml-divider-leaf my-6 text-xs">or email & password</div>

          <form onSubmit={submit} className="space-y-4 wizard-readable">
            <div>
              <label className="ml-label">Email</label>
              <div className="relative">
                <Mail className="w-4 h-4 absolute left-4 top-1/2 -translate-y-1/2 text-[hsl(var(--primary)/0.55)]" />
                <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                  className="ml-input pl-11" placeholder="you@example.com" data-testid="login-email-input" />
              </div>
            </div>
            <div>
              <label className="ml-label">Password</label>
              <div className="relative">
                <Lock className="w-4 h-4 absolute left-4 top-1/2 -translate-y-1/2 text-[hsl(var(--primary)/0.55)]" />
                <input type={showPassword ? 'text' : 'password'} required value={password} onChange={(e) => setPassword(e.target.value)}
                  className="ml-input pl-11 !pr-10" placeholder="••••••••" data-testid="login-password-input" />
                <button type="button" onClick={() => setShowPassword((s) => !s)} className="absolute right-1 top-1/2 -translate-y-1/2 p-2 text-[hsl(var(--primary)/0.55)] hover:text-[hsl(var(--primary))] transition-colors" aria-label={showPassword ? 'Hide password' : 'Show password'} data-testid="login-password-toggle" tabIndex={-1}>
                  {showPassword ? <EyeOff size={18}/> : <Eye size={18}/>}
                </button>
              </div>
            </div>
            {err && <div className="text-sm text-[hsl(var(--destructive))]" data-testid="login-error">{err}</div>}
            <div className="flex items-center justify-between gap-3">
              <label className="inline-flex items-center gap-2 cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={remember}
                  onChange={(e) => setRemember(e.target.checked)}
                  data-testid="login-remember-checkbox"
                  className="w-4 h-4 accent-[hsl(var(--primary))] rounded border-[hsl(var(--primary)/0.3)] cursor-pointer"
                />
                <span className="text-[13px] text-[hsl(var(--primary)/0.85)] font-medium">
                  Remember my email
                </span>
              </label>
              <Link
                to="/forgot-password"
                className="text-[12px] tracking-[0.18em] uppercase font-semibold text-[hsl(var(--primary)/0.7)] hover:text-[hsl(var(--primary))] transition-colors"
                data-testid="login-forgot-link"
              >
                Forgot password?
              </Link>
            </div>
            <button type="submit" disabled={loading} className="ml-btn-primary w-full !py-3.5" data-testid="login-submit-btn">
              {loading ? 'Signing in…' : 'Sign in'}
            </button>
          </form>

          <p className="text-sm text-[hsl(var(--primary)/0.78)] mt-6 font-medium">
            New to MyLeaf? <Link to="/register" className="text-[hsl(var(--primary))] font-semibold" data-testid="login-register-link">Create a memorial →</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
